import React from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives/responsive";

import { spaceTokenValues } from "../../utils";

import { ListItemNumber } from "./list-item-number.tsx";

export type ListAs = "ul" | "ol" | "dl";

export interface UniformListV2Props {
    listType: ListAs;
    gap?: keyof typeof spaceTokenValues;
}

export const UniformListV2: React.FC<UniformListV2Props> = ({
    gap = "space.0",
    ...props
}) => {
    const spaceTokenValue = spaceTokenValues[gap];

    const listStyle = css({
        display: "flex",
        flexDirection: "column",
        gap: "var(--ds-space-" + spaceTokenValue + ")"
    });

    const { listType: ListTypeComponent = "ul" } = props;

    const zeroMarginPadding = css({
        margin: 0,
        padding: 0
    });

    const renderListItems: React.ComponentType<
        UniformSlotWrapperComponentProps
    > = ({ items }) => {
        if (props.listType === "dl") {
            // Only works for paired dt/dd items. Would need different solution for different structure
            return items.map((item, index) => {
                if (index % 2 === 0) {
                    return (
                        <React.Fragment key={index}>
                            <dt>{item}</dt>
                            {items[index + 1] && <dd>{items[index + 1]}</dd>}
                        </React.Fragment>
                    );
                }
                return null;
            });
        }

        if (props.listType === "ol") {
            return items.map((item, index) => (
                <li
                    key={index}
                    css={[
                        zeroMarginPadding,
                        css({
                            listStyle: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            [media.above.sm]: {
                                flexDirection: "row",
                                alignItems: "baseline"
                            }
                        })
                    ]}
                >
                    <ListItemNumber value={index + 1} />
                    {item}
                </li>
            ));
        }

        return items.map((item, index) => (
            <li key={index} css={[css({ all: "unset" }), zeroMarginPadding]}>
                {item}
            </li>
        ));
    };

    return (
        <ListTypeComponent css={[listStyle, zeroMarginPadding]}>
            <UniformSlot name="listItems" wrapperComponent={renderListItems} />
        </ListTypeComponent>
    );
};

export default UniformListV2;
